import {
  LitElement,
  html,
  css,
  classMap,
  paths,
  t,
  unsafeHTML,
} from '@hh/global.js'

class HHMegamenu extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    activeThirdLevel: {
      type: String,
      attribute: 'active-third-level',
    },
    firstLevelImage: {
      type: String,
      attribute: 'first-level-image',
    },
    currentFeaturedImage: {
      type: String,
    },
    currentFeaturedHref: {
      type: String,
    },
    currentFeaturedLinkText: {
      type: String,
    },
    hideFeaturedImage: {
      type: Boolean,
    },
  }

  constructor() {
    super()

    this.hideFeaturedImage = true
    this.currentFeaturedLinkText = ''

    this.classes = {
      open: 'open',
    }
  }

  get firstLevelButton() {
    const slot = this.shadowRoot?.querySelector(`slot[name='first-level']`)
    return slot?.assignedNodes()[0] ?? null
  }

  get image() {
    return this.shadowRoot?.querySelector('.image') ?? null
  }

  get thirdLevels() {
    const slot = this.shadowRoot.querySelector(`slot[name='third-level']`)
    return slot?.assignedNodes() ?? []
  }

  get secondLevelLinks() {
    const slot = this.shadowRoot?.querySelector(`slot[name='second-level']`)
    const slots = slot?.assignedNodes() ?? []
    return slots.map((slot) => [...slot.querySelectorAll('hh-button')]).flat()
  }

  get thirdLevelLinks() {
    const slot = this.shadowRoot?.querySelector(`slot[name='third-level']`)
    const slots = slot?.assignedNodes() ?? []
    return slots.map((slot) => [...slot.querySelectorAll('hh-button')]).flat()
  }

  connectedCallback() {
    super.connectedCallback()
    this.currentFeaturedImage = this.firstLevelImage

    window.addEventListener('megamenu:open', this.onMegamenuOpen.bind(this))
    window.addEventListener('megamenu:close-all', this.closeMenu.bind(this))
    window.addEventListener('search:opened', this.closeMenu.bind(this))
    window.addEventListener('keydown', this.onEscKey.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.thirdLevelLinks.forEach((link) => {
      link.removeEventListener('mouseover', this.onThirdLevelMouseOver)
    })

    window.removeEventListener('megamenu:open', this.onMegamenuOpen)
    window.removeEventListener('megamenu:close-all', this.closeMenu)
    window.removeEventListener('search:opened', this.closeMenu)
    window.removeEventListener('keydown', this.onEscKey)
  }

  firstUpdated() {
    this.thirdLevelLinks.forEach((link) => {
      link.addEventListener('mouseover', this.onThirdLevelMouseOver.bind(this))
      link.addEventListener('focusin', this.onThirdLevelMouseOver.bind(this))
    })
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open) {
        this.dispatchMenuEvent()
      }
      this.toggleAriaExpanded()
    }

    if (changedProperties.has('activeThirdLevel')) {
      this.updateThirdLevel()
    }
  }

  // Handlers
  onMegamenuOpen(e) {
    if (e.target === this) return
    this.closeMenu()
  }

  onCloseClick(e) {
    e.preventDefault()
    this.closeMenu()
  }

  onFirstLevelClick(e) {
    e.preventDefault()
    if (document.documentElement.classList.contains('search-open')) {
      this.dispatchEvent(
        new CustomEvent('search:close', {
          bubbles: true,
          composed: true,
        })
      )
    }
    this.openMenu()
  }

  onFirstLevelMouseEnter(e) {
    e.preventDefault()
    if (document.documentElement.classList.contains('search-open')) return
    this.openMenu()
  }

  async onSecondLevelClick(e) {
    const { target } = e
    const { childMenu } = target.dataset
    this.currentFeaturedLinkText = ''
    if (childMenu) {
      e.preventDefault()
      this.removeAllAriaExpandedSecondLevel(target)
      target.setAttribute('aria-expanded', 'true')
      this.activeThirdLevel = childMenu

      const { featuredImage } = target.dataset
      if (featuredImage) {
        const validImage = await this.checkIfImageExists(featuredImage)
        if (validImage) {
          this.updateFeaturedImage(featuredImage)
        } else {
          this.updateFeaturedImage(this.firstLevelImage)
        }
      }
    }
  }

  async onThirdLevelMouseOver(e) {
    const { target } = e
    const { featuredImage } = target.dataset
    if (featuredImage) {
      const validImage = await this.checkIfImageExists(featuredImage)
      if (validImage) {
        this.updateFeaturedImage(featuredImage)
      }
    }

    this.currentFeaturedHref = target.href
    this.currentFeaturedLinkText = target.textContent
  }

  onEscKey(e) {
    if (e.key === 'Escape' && this.open) {
      this.closeMenu()
      this.firstLevelButton.focus()
    }
  }

  onOverlayMouseEnter(e) {
    e.preventDefault()
    clearTimeout(this.menuLeaveTimeout)
    this.menuLeaveTimeout = setTimeout(() => {
      this.closeMenu()
    }, 1500)
  }

  onOverlayMouseLeave(e) {
    e.preventDefault()
    clearTimeout(this.menuLeaveTimeout)
  }

  onImageError() {
    this.hideFeaturedImage = true
  }

  // Helpers
  openMenu() {
    this.open = true
    this.hideFeaturedImage = false
    this.currentFeaturedLinkText = ''
    this.updateHeaderHeight()
  }

  closeMenu() {
    this.open = false
    this.hideFeaturedImage = true
    this.currentFeaturedLinkText = ''
    this.currentFeaturedImage = this.firstLevelImage
    this.activeThirdLevel = null
  }

  checkIfImageExists(src) {
    return new Promise((resolve) => {
      const img = new Image()
      img.src = src
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
    })
  }

  updateFeaturedImage(src) {
    if (this.currentFeaturedImage === src) return
    this.hideFeaturedImage = true
    setTimeout(() => {
      this.currentFeaturedImage = src
      this.hideFeaturedImage = false
    }, 50)
  }

  updateThirdLevel() {
    this.thirdLevels.forEach((thirdLevel) => {
      if (thirdLevel.dataset.childMenu === this.activeThirdLevel) {
        thirdLevel.classList.add(this.classes.open)
        thirdLevel.setAttribute('tabindex', '0')
        thirdLevel.focus({
          preventScroll: true,
        })
      } else {
        thirdLevel.classList.remove(this.classes.open)
        thirdLevel.removeAttribute('tabindex')
      }
    })
  }

  dispatchMenuEvent() {
    this.dispatchEvent(
      new CustomEvent('megamenu:open', {
        bubbles: true,
        composed: true,
      })
    )
  }

  closeAllMenus() {
    this.dispatchEvent(
      new CustomEvent('megamenu:close-all', {
        bubbles: true,
        composed: true,
      })
    )
  }

  toggleAriaExpanded() {
    this.firstLevelButton?.setAttribute('aria-expanded', this.open)
  }

  removeAllAriaExpandedSecondLevel(exceptionElement) {
    this.secondLevelLinks.forEach((link) => {
      if (link !== exceptionElement && link.hasAttribute('aria-expanded')) {
        link.setAttribute('aria-expanded', 'false')
      }
    })
  }

  updateHeaderHeight() {
    this.headerHeight = this.header.offsetHeight
    this.announcementBarHeight = this.announcementBar?.offsetHeight || 0
    this.totalHeaderHeight = this.headerHeight + this.announcementBarHeight

    this.style.setProperty('--hh-header-height', `${this.totalHeaderHeight}px`)
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar-section') ?? null
  }

  render() {
    return html`
      <div
        class=${classMap({
          overlay: true,
          active: this.open,
        })}
        @mouseenter=${this.onOverlayMouseEnter}
        @mouseleave=${this.onOverlayMouseLeave}
        aria-hidden="true"
      ></div>
      <slot
        name="first-level"
        @click=${this.onFirstLevelClick}
        @mouseenter=${this.onFirstLevelMouseEnter}
      ></slot>
      <div
        class=${classMap({
          menu: true,
          open: this.open,
        })}
      >
        <div class="menu-inner">
          <div class="left">
            <slot name="second-level" @click=${this.onSecondLevelClick}></slot>
            <slot name="third-level"></slot>
          </div>

          <div class="right">
            <img
              class=${classMap({
                image: true,
                hide: this.hideFeaturedImage,
              })}
              src=${this.currentFeaturedImage}
              alt=${this.currentFeaturedLinkText}
              loading="lazy"
              @error=${this.onImageError}
            />
            <a
              class=${classMap({
                link: true,
                hide: this.currentFeaturedLinkText === '',
              })}
              tabindex="-1"
              href=${this.currentFeaturedHref}
            >
              <span class="link-text">
                ${t('accessibility.go_to')} ${this.currentFeaturedLinkText}
                ${unsafeHTML(paths.longArrow)}
              </span>
            </a>
          </div>
          <button
            class="close"
            aria-label="${t('accessibility.close')}"
            @click=${this.onCloseClick}
          >
            ${unsafeHTML(paths.close)}
          </button>
        </div>
      </div>
    `
  }

  static styles = css`
    :host {
      display: block;
      z-index: 100;
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .overlay {
      position: fixed;
      display: none;
      top: var(--hh-header-height, 0);
      left: 0;
      right: 0;
      bottom: 0;
      width: 100vw;
      height: 120vh;
      background-color: rgba(0, 0, 0, 0.25);
      z-index: -1;
    }

    .overlay.active {
      display: block;
    }

    .menu {
      display: none;
      position: absolute;
      top: 100%;
      width: 100%;
      left: 0;
      right: 0;
    }

    .menu-inner {
      max-width: 140rem;
      margin: 0 auto;
      background: var(--hh-color-day);
      border-radius: 0 0 2rem 2rem;
      display: flex;
      position: relative;
    }

    .left {
      width: 50%;
      display: flex;
      justify-content: space-between;
    }

    ::slotted([slot='second-level']) {
      width: 50%;
    }

    ::slotted([slot='third-level']) {
      width: 50%;
    }

    .menu.open {
      display: block;
    }

    .third-level {
      display: none;
    }

    .right {
      width: 50%;
      position: relative;
      overflow: hidden;
      border-radius: 0 0 2rem;
    }

    .image {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform var(--hh-duration-long) ease-in-out;
      animation: fadeIn var(--hh-duration-medium-long) forwards;
    }

    .image.hide {
      display: none;
    }

    .right:hover .image {
      transform: scale(1.08);
    }

    .right:hover .link {
      opacity: 1;
    }

    .link {
      position: absolute;
      inset: 0;
      z-index: 3;
      opacity: 0;
      transition: opacity var(--hh-duration-medium-long) ease-in-out;
    }

    .link.hide {
      display: none;
    }

    .link:focus-visible {
      opacity: 1;
    }

    .link:focus-visible .link-text {
      text-decoration: underline;
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .link-text {
      position: absolute;
      right: var(--hh-spacing-xs);
      bottom: var(--hh-spacing-md);
      color: var(--hh-color-day);
      font: var(--hh-body-sm-font);
      display: flex;
      align-items: center;
      gap: var(--hh-spacing-xxs);
    }

    .link-text .icon {
      width: 5rem;
    }

    .link-text:hover {
      text-decoration: underline;
      text-underline-offset: var(--hh-spacing-xxs);
    }

    .close {
      appearance: none;
      border: none;
      background: var(--hh-color-day);
      padding: 0;
      position: absolute;
      top: var(--hh-spacing-md);
      right: var(--hh-spacing-2xl);
      cursor: pointer;
      border-radius: 50%;
      z-index: 4;
      width: 4.2rem;
      height: 4.2rem;
      padding: 1rem;
      color: var(--hh-color-brand-secondary);
      display: flex;
      justify-content: center;
      align-items: center;
      transition: transform var(--hh-duration-default) ease-in;
    }

    .close:hover {
      transform: rotate(-180deg);
    }

    .close .icon {
      width: 100%;
      height: 100%;
    }

    @keyframes fadeIn {
      0% {
        opacity: 0;
      }

      100% {
        opacity: 1;
      }
    }
  `
}

customElements.define('hh-megamenu', HHMegamenu)
